import { Injectable } from '@angular/core';
import { EdlEntriesFormBuilderService, EdlEntriesFormGroup, EdlEntry, EDLGroupType, EdlPortalUserSearchCriteria, Group } from '@edl-development/edl-common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToggleGroupModalComponent } from '../modals/toggle-group-modal/toggle-group-modal.component';
import { UpdateGroupsForUserModel } from '../models/update-groups-for-user.model';

@Injectable()
export class SelectGroupService {

    constructor(
        private edlEntryFormBuilderService: EdlEntriesFormBuilderService,
        private ngbModal: NgbModal
    ) { }

   /**
     * Builds a form group containing only the selected groups, filtered by type if specified.
     * @param selectedGroupIds Array of group IDs to select.
     * @param searchCriteria Criteria containing all available portal groups.
     * @param filterGroupType Optional group type to filter by.
     * @returns A form group with the selected groups.
     */
    public toGroupsSelectForm({
        selectedGroupIds,
        searchCriteria,
        filterGroupType
    }: {
        selectedGroupIds: string[],
        searchCriteria: EdlPortalUserSearchCriteria,
        filterGroupType?: EDLGroupType
    }) {
        const filteredGroups = this.filterPortalGroups({ searchCriteria, filterGroupType }).filter(g => selectedGroupIds.includes(g.GroupID));

        const entries: EdlEntry<Group>[] = filteredGroups.map(group => ({ id: group.GroupID, name: group.GroupName, details: group }));
        return this.edlEntryFormBuilderService.buildSelectedFormGroup(entries);
    }

    public toUpdateGroupsForUser({
        userId,
        currentGroups = [],
        updatedGroups = [],
        filterGroupType
    }: {
        userId: string,
        currentGroups: Group[]
        updatedGroups: Group[],
        filterGroupType?: EDLGroupType
    }): UpdateGroupsForUserModel {
        const filteredCurrentGroups = this.filterGroups({ groups: currentGroups, filterGroupType });
        const filteredUpdatedGroups = this.filterGroups({ groups: updatedGroups, filterGroupType });

        const groupsToAdd = filteredUpdatedGroups.filter(g => !filteredCurrentGroups.some(cg => cg.GroupID === g.GroupID));
        const groupsToRemove = filteredCurrentGroups.filter(cg => !filteredUpdatedGroups.some(g => g.GroupID === cg.GroupID));

        return {
            UserId: userId,
            GroupIdsToAdd: groupsToAdd.map(g => g.GroupID),
            GroupIdsToRemove: groupsToRemove.map(g => g.GroupID)
        };
    }

    /**
     * Opens a modal to allow the user to filter and select groups.
     * Updates the provided form group with the user's selection.
     * @param groupsForm The form group to update with selected groups.
     * @param searchCriteria Criteria containing all available portal groups.
     * @param filterGroupType Optional group type to filter by.
     * @param keepFilteredOutGroups If true, keeps previously selected groups that were excluded from the group selection modal.
     *          For Example, if keepFilteredOutGroups is true, and filterGroupType is set to EDLGroupType.GROUP,
     *          then the form will retain any volunteer areas that were previously selected.
     * @returns A promise that resolves when the modal is closed.
     */
    public openFilterGroupsModal({
        groupsForm,
        groups = [],
        filterGroupType,
        keepFilteredOutGroups = false
    }: {
        groupsForm: EdlEntriesFormGroup<Group>, 
        groups: Group[],
        filterGroupType?: EDLGroupType,
        keepFilteredOutGroups?: boolean
    }) {
        // Filters out the deleted groups and sets the IsSelected property to true if the group is selected
        const filteredGroups: Group[] = this.filterGroups({ groups, filterGroupType })
            .map(g => Object.assign({}, g, { IsSelected: !!groupsForm?.get(g.GroupID) }));
        const groupIds = filteredGroups.map(g => g.GroupID);

        // If keepFilteredOutGroups is true, we need to retain the previously selected entries that are not in the groups list.
        const currentSelectedEntries = this.edlEntryFormBuilderService.toSelectedEntries<Group>(groupsForm);
        const filteredOutSelectedEntries = currentSelectedEntries.filter(entry => !groupIds.includes(entry.id));

        const title = `Select ${this.getTypeLabel(filterGroupType)}`;
    
        return this.openToggleGroupModal({ groups: filteredGroups, filterGroupType, title })
            .then((selectedGroups: Group[]) => {
                // Update the form with the selected groups
                const entries: EdlEntry<Group>[] = selectedGroups
                    .filter(group => group.IsSelected)
                    .map(group => ({ id: group.GroupID, name: group.GroupName, details: group }));
                
                // If keepFilteredOutGroups is true, we merge the selected entries with the previously filtered out entries.
                // Otherwise, we only use the newly selected entries.
                const selectedFilteredOutEntries = keepFilteredOutGroups ? filteredOutSelectedEntries : [];
                const mergedEntries: EdlEntry<Group>[] = [...entries, ...selectedFilteredOutEntries];
    
                this.edlEntryFormBuilderService.updateSelectedFormGroup(groupsForm, mergedEntries);
            }, () => { });
    }

    
    /**
     * Opens the ToggleGroupModalComponent modal for group selection.
     * @param groups Array of groups to display in the modal.
     * @param filterGroupType Optional group type to filter by in the modal.
     * @returns A promise that resolves with the selected groups when the modal is closed.
     */
    public openToggleGroupModal({
        groups,
        filterGroupType,
        title = 'Select Groups to Update'
    }: {
        groups: Group[], 
        filterGroupType?: EDLGroupType
        title?: string
    }) {
        let modalRef = this.ngbModal.open(ToggleGroupModalComponent, {
            size: 'xl',
            backdrop: 'static'
        });
        modalRef.componentInstance.groups = groups;
        modalRef.componentInstance.filterGroupType = filterGroupType;
        modalRef.componentInstance.title = title;
        return modalRef.result;
    }

    /**
     * Returns all non-deleted groups from the search criteria, filtered by type if specified.
     * @param searchCriteria Criteria containing all available portal groups.
     * @param filterGroupType Optional group type to filter by.
     * @returns Array of filtered groups.
     */
    public filterPortalGroups = ({ 
        searchCriteria,
        filterGroupType
    } : {
        searchCriteria: EdlPortalUserSearchCriteria, 
        filterGroupType?: EDLGroupType
    }) => this.filterGroups({ groups: searchCriteria.PortalGroups, filterGroupType });

    /**
     * Returns all non-deleted groups from the search criteria, filtered by type if specified.
     * @param groups Groups to be filtered
     * @param filterGroupType Optional group type to filter by.
     * @returns Array of filtered groups.
     */
    public filterGroups = ({ 
        groups,
        filterGroupType
    } : {
        groups: Group[], 
        filterGroupType?: EDLGroupType
    }) => {
        const hasGroupTypeFilter = filterGroupType !== undefined && filterGroupType !== null;
        return groups
            .filter(g => !g.IsDeleted && (!hasGroupTypeFilter || g.Type === filterGroupType));
    }

    public getTypeLabel(type: EDLGroupType): string {
        switch (type) {
            case EDLGroupType.VOLUNTEER_AREA:
                return "Volunteer Areas";
            case EDLGroupType.GROUP:
            default:
                return "Groups";
        }
    }
}
